import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { InvestorProfile } from '@common/interfaces/investor-profile';
import { IProductDetailsResponse } from '@common/interfaces/product-details';
import { IProductsResponse } from '@common/interfaces/products';
import { ISuitabilityDetails } from '@common/interfaces/suitability-details';
import { IInvestmentAcknowledgementResponse } from '@common/interfaces/investments';
import { IStatisticsResponse } from '@common/interfaces/statistics';
import { environment } from '@environment/environment';
import { BehaviorSubject, Observable, of, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  investorProfile = new BehaviorSubject<InvestorProfile | undefined>(undefined);
  productDetailsResponse = new BehaviorSubject<
    IProductDetailsResponse | undefined
  >(undefined);
  redirectToProduct?: boolean;
  approvedRiskDate?: string;

  constructor(private http: HttpClient) {}

  fetchInvestorProfileData(): Observable<InvestorProfile> {
    if (this.investorProfile.value) {
      return of(this.investorProfile.value);
    }

    return this.getInvestorProfile().pipe(
      tap(profile => {
        this.investorProfile.next(profile);
      })
    );
  }

  getInvestorProfile(): Observable<InvestorProfile> {
    return this.http.get<InvestorProfile>(
      `${environment.baseUrl}api/v1/investors/profile`
    );
  }

  getProducts(page: string): Observable<IProductsResponse> {
    const params = new HttpParams().append('page', page);
    return this.http.get<IProductsResponse>(
      `${environment.baseUrl}api/v1/products`,
      { params }
    );
  }

  getProductDetails(productID: number): Observable<IProductDetailsResponse> {
    return this.http.get<IProductDetailsResponse>(
      `${environment.baseUrl}api/v1/products/${productID}`
    );
  }

  downloadAttachment(url: string): Observable<Blob> {
    return this.http.get(url, {
      responseType: 'blob',
    });
  }

  getInvestmentAcknowledgement(
    productId: number
  ): Observable<IInvestmentAcknowledgementResponse> {
    return this.http.get<IInvestmentAcknowledgementResponse>(
      `${environment.baseUrl}/api/v1/products/${productId}/acknowledgment`
    );
  }

  getSuitabilityDetails(productId: number): Observable<ISuitabilityDetails> {
    return this.http.get<ISuitabilityDetails>(
      `${environment.baseUrl}/api/v1/products/${productId}/suitability`
    );
  }

  checkIsProductInvestable(productId: number): Observable<any> {
    return this.http.get<any>(
      `${environment.baseUrl}/api/v1/products/${productId}/investable`
    );
  }

  getStatistics(): Observable<IStatisticsResponse> {
    return this.http.get<IStatisticsResponse>(
      `${environment.baseUrl}api/v1/investments/statistics`
    );
  }
}
